import React, { useRef, useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import {
	GlobalSideNavigation,
	type ForYouConfig,
	type ItemsConfig,
} from '@atlassian/global-side-navigation';
import type { CustomItemProps, ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import {
	SidebarNav4ContextProvider,
	useSidebarNav4,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { Nav4Filters } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-filters/src/index.tsx';
import {
	L1_MENU_ID,
	type JiraConfigurableNavigationItem,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { Nav4Apps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { CustomerService } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Nav4Dashboards } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Operations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Overviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/index.tsx';
import { Nav4Plans } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Recent } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/index.tsx';
import { Nav4Roadmaps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Starred } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/index.tsx';
import { Teams } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useShouldRenderOverviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/controllers/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import {
	USER_NAVIGATION_CONFIGURATION_DEFAULT,
	USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { Stub } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/stub/main.tsx';
import { navPlatformSidebarResource } from '@atlassian/jira-nav-platform-integration-resources/src/services/sidebar/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { isTeamsAppEnabled } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { getConfigurableMessageAndIcon } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { isConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/is-configurable-menu-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { RECENT_QUERY_LIMIT } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';


import { getForYouConfig } from '../common/ui/for-you/getForYouConfig.tsx';
import type {
	GSNForContentSidebarQuery,
	GSNForContentSidebarQuery$data,
} from './__generated__/GSNForContentSidebarQuery.graphql';

export function GSNForContentSidebar(): React.JSX.Element | null {
	/*
		TODO: Add support for entryPoint routes

	*/
	// This is the non-EP solution for loading the data
	const { queryReference } = useRelayResource<GSNForContentSidebarQuery>(
		navPlatformSidebarResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GSNForContentSidebarQuery> | null>(null);

	// We don't want to use nullable query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	} else if (!queryReferenceRef.current) {
		return null;
	}

	return (
		<SidebarNav4ContextProvider>
			<GSNForContentSidebarInner queryReference={queryReferenceRef.current} />
		</SidebarNav4ContextProvider>
	);
}

type Props = {
	queryReference: PreloadedQuery<GSNForContentSidebarQuery>;
};

function GSNForContentSidebarInner(props: Props) {
	const { queryReference } = props;

	const queryRef = usePreloadedQuery<GSNForContentSidebarQuery>(
		// Need to disable this rule for the useNetworkSideNavUserData fragment (which is inside GSN)
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		graphql`
			query GSNForContentSidebarQuery(
				$cloudId: ID!
				$entityAriProjects: ID!
				$entityAriUserSideNavSettings: ID!
				$isAppsExpanded: Boolean = false
				$isFiltersExpanded: Boolean = false
				$isPlansExpanded: Boolean = false
				$isProjectsExpanded: Boolean = false
				$isOperationsExpanded: Boolean = false
				$isOverviewsExpanded: Boolean = false
				$navPlatMenuDataEntityAri: ID!
				$navPlatSidebarDataEntityAri: ID!
				$ownerAri: ID
			) {
				...useNetworkSideNavUserData
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Overviews
				...Nav4Plans
				...Nav4Projects
			}
		`,
		queryReference,
	);
	const shouldRenderOverviews = useShouldRenderOverviews();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const ownerId = useAccountId();
	const { isSelectedPath } = useSidebarNav4();

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out,
	//
	const customizationStore = 'network-migration';

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		expVal('navx-1982-change-order-of-teams-and-goals', 'isEnabled', false)
			? USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED.navItems
			: USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
	);

	const items = useItemConfig(navItems, queryRef, shouldRenderOverviews);
	const recentEntryPointParams = useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]);
	const forYouConfig = useMemo(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		() => getForYouConfig({ recentEntryPointParams }) as ForYouConfig<ObjectListFlyoutItem>,
		[recentEntryPointParams],
	);

	// Determine peekMenuId from selectedPath
	const peekMenuId = navItems.find((item) => isSelectedPath(item.menuId))?.menuId;

	const networkStorageParams = useMemo(() => {
		const siteId = cloudId;
		const entityAri = JiraWorkspaceAri.create({ siteId, activationId }).toString();
		const ownerAri = (ownerId && IdentityUserAri.create({ userId: ownerId }).toString()) || '';

		return { activationId, entityAri, ownerAri, siteId, queryRef };
	}, [cloudId, activationId, ownerId, queryRef]);

	return (
		<GlobalSideNavigation
			app="jira"
			appLabel="Jira"
			customizationStore={customizationStore}
			forYou={forYouConfig}
			items={items}
			peekMenuId={peekMenuId}
			networkStorageParams={networkStorageParams}
			testId="nav-platform-integration-sidebar.ui.global-side-navigation"
		/>
	);
}

type ComponentProps = {
	queryRef: GSNForContentSidebarQuery$data;
	isExpanded: boolean;
};

function useItemConfig(
	navItems: JiraConfigurableNavigationItem[],
	queryRef: GSNForContentSidebarQuery$data,
	shouldRenderOverviews: boolean,
) {
	const { formatMessage } = useIntl();

	const components = useMemo(() => {
		const componentProps: ComponentProps = { isExpanded: false, queryRef };
		const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

		return navItems.map(({ menuId }) => {
			switch (menuId) {
				case L1_MENU_ID.APPS:
					return <Nav4Apps {...componentProps} />;
				case L1_MENU_ID.ASSETS:
					/* remove this case on
					 * assets_as_an_app_v2 cleanup
					 */
					return <Nav4Assets isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.DASHBOARDS:
					return <Nav4Dashboards isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.FILTERS:
					return <Nav4Filters {...componentProps} />;
				case L1_MENU_ID.GOALS:
					return (
						<Stub menuId={L1_MENU_ID.GOALS} href={USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS]} />
					);
				case L1_MENU_ID.OPERATIONS:
					return <Nav4Operations {...componentProps} />;
				case L1_MENU_ID.PLANS:
					return <Nav4Plans {...componentProps} />;
				case L1_MENU_ID.OVERVIEWS:
					return shouldRenderOverviews ? <Nav4Overviews {...componentProps} /> : null;
				case L1_MENU_ID.PROJECTS:
					return <Nav4Projects {...componentProps} />;
				case L1_MENU_ID.RECENT:
					return <Recent />;
				case L1_MENU_ID.ROADMAPS:
					return <Nav4Roadmaps isExpanded={componentProps.isExpanded} />;
				case L1_MENU_ID.STARRED:
					return <Starred />;
				case L1_MENU_ID.TEAMS:
					return !isTeamsAppEnabled() ? <Teams /> : null;
				case L1_MENU_ID.CUSTOMER_SERVICE:
					return fg('jcs_master_flag') ? <CustomerService /> : null;
				default:
					return null;
			}
		});
	}, [navItems, queryRef, shouldRenderOverviews]);

	return useMemo(
		() =>
			navItems.reduce<ItemsConfig>((acc, navItem, index) => {
				const component = components[index];

				// We need a configurable menu id and a component to continue
				if (isConfigurableMenuId(navItem.menuId) && component) {
					const { message, Icon } = getConfigurableMessageAndIcon(navItem.menuId);
					const label = formatMessage(message);

					// See src/packages/navigation-apps/sidebar-nav4/sidebars/common/core/src/common/utils/use-formatted-message-and-icon/index.tsx
					const elemBefore = <Icon label={label} spacing="spacious" color="currentColor" />;

					acc[navItem.menuId] = {
						type: 'custom',
						elemBefore,
						label,
						Component: ({ isInMoreMenu, onClick, onPeek }: CustomItemProps) => {
							// When the item isInMoreMenu, we cannot render directly as this breaks the Peek logic.
							if (isInMoreMenu) {
								// This is the same transformation happening inside GSN
								const handleClick = () => {
									onPeek?.();
									onClick?.();
								};

								// todo: We should handle this internally in GSN to avoid duplicating this logic
								return (
									<ButtonMenuItem onClick={handleClick} elemBefore={elemBefore}>
										{label}
									</ButtonMenuItem>
								);
							}
							return component;
						},
					};
				}

				return acc;
			}, {}),
		[components, formatMessage, navItems],
	);
}
